import { ref, onMounted, onBeforeUnmount, computed } from 'vue';
import { useRoute } from 'vue-router';
import isString from 'lodash/isString';
import createBus from '~/support/utils/event-bus';
import type { FilterQuery, FilterConfig, FilterTree } from '@monolog/shared/types';
import { map, lastValueFrom } from 'rxjs';
import { useFiltersStore } from '~/store/filters';
import { useQueryManager } from '~/composables/useQueryManager';
import { buildFilterTree, flattenTreeToFilters } from '../support/utils/filters.utils';
import isEqual from 'lodash/isEqual';
import { FilterOptions, FilterType } from '@constants';

const FilterEvents = {
  CHANGE: 'change',
  LOADED: 'loaded',
};

export function useFilters(type: FilterType, opts?: FilterOptions) {
  const $bus = createBus();
  const route = useRoute();
  const { filters: filtersSvc } = useGateway();
  const filtersStore = useFiltersStore();
  const availableFilters = ref<FilterConfig[]>([]);
  const filtersLoaded = ref(false);
  const isLoading = ref(false);
  const { setQueryParams } = useQueryManager();

  const appliedFilters = computed({
    get: () => filtersStore.getFilters(type),
    set: val => filtersStore.setFilters(type, val),
  });

  const options = computed(() => {
    return {
      addedFilters: opts?.addFilterFn ? opts.addFilterFn(availableFilters.value, appliedFilters.value) : [],
      hideAddButton: !!opts?.hideAddButton,
      hideClearButton: !!opts?.hideClearButton,
      hideDeleteButton: !!opts?.hideDeleteButton,
      hideOperator: !!opts?.hideOperator,
      startTitle: opts?.startTitle,
    };
  });

  const applyFilters = (filters: FilterQuery[], previousFilters?: FilterQuery[]) => {
    const enrichedFilters = filters.map((filter, index) => {
      const type = filter.type || availableFilters.value.find(f => f.field === filter.field)?.type || 'string';

      return {
        ...filter,
        logicalOperator: index === 0 ? undefined : filter.logicalOperator,
        type,
      };
    });

    const previous = previousFilters ?? filtersStore.getFilters(type);
    const filtersChanged = !isEqual(previous, enrichedFilters);

    appliedFilters.value = enrichedFilters;

    const tree = buildFilterTree(enrichedFilters);
    if (enrichedFilters.length > 0) {
      setQueryParams({ filters: encodeURIComponent(JSON.stringify({ routeType: type, tree })) });
    } else if (ownsUrlFilters()) {
      // Only clear the param when it is ours. `filters` is one key shared by every
      // useFilters instance, and several are alive at once — the side nav holds a
      // FilterType.claim one on every page. Clearing unconditionally means any
      // filterless instance erases another page's deep link the moment it mounts.
      setQueryParams({ filters: null });
    }

    if (filtersChanged) {
      $bus.emit(FilterEvents.CHANGE, { filters: enrichedFilters, type });
    }
  };

  const clearFilters = () => {
    const current = filtersStore.getFilters(type);
    applyFilters([], current);
  };

  /**
   * True when the URL's shared `filters` param is this instance's to clear. `filters` is
   * a single key every useFilters shares, so ownership has to be checked before removing
   * it. An untagged param (no routeType) counts as ours, mirroring
   * `extractFiltersFromRoute`, which reads those for any type — whatever we would consume
   * we may also clean up.
   */
  const ownsUrlFilters = (): boolean => {
    const raw = route.query.filters;
    if (!isString(raw)) return false;

    try {
      const { routeType } = JSON.parse(decodeURIComponent(raw));

      return !routeType || routeType === type;
    } catch {
      return false;
    }
  };

  const removeFilter = (index: number) => {
    const current = filtersStore.getFilters(type);
    if (index >= 0 && index < current.length) {
      const next = [...current.slice(0, index), ...current.slice(index + 1)];
      applyFilters(next, current);
    }
  };

  const fetchAvailableFilters = async () => {
    if (!type) return;

    isLoading.value = true;

    try {
      const fetchedFilters = await lastValueFrom(
        filtersSvc.filters.ofType(type).pipe(map((res: { data: FilterConfig[] }) => res.data)),
      );
      availableFilters.value = fetchedFilters as FilterConfig[];
      filtersLoaded.value = true;

      $bus.emit(FilterEvents.LOADED, { filterOptions: fetchedFilters, type });
    } catch (error) {
      console.error('Failed to fetch available filters:', error);
    } finally {
      isLoading.value = false;
    }
  };

  const extractFiltersFromRoute = (): FilterQuery[] => {
    const rawFilters = route.query.filters;
    if (!isString(rawFilters)) return [];

    try {
      const decoded = decodeURIComponent(rawFilters);
      const parsed = JSON.parse(decoded);

      if (parsed.routeType && parsed.routeType !== type) return [];

      const flatFilters = Array.isArray(parsed) ? parsed : parsed.tree ? flattenTreeToFilters(parsed.tree) : [];

      return flatFilters.map((filter: any, index) => {
        const typeFromMeta =
          filter.type || availableFilters.value.find(f => f.field === filter.field)?.type || 'string';

        return {
          ...filter,
          logicalOperator: index === 0 ? undefined : filter.logicalOperator,
          type: typeFromMeta,
          value: castValue(filter.value, typeFromMeta),
        };
      });
    } catch (err) {
      console.error('Failed to parse filters from route:', err);

      return [];
    }
  };

  const castValue = (value: any, type: string): any => {
    switch (type.toLowerCase()) {
      case 'int':
      case 'float':
      case 'number':
      case 'decimal':
        return Number(value);
      case 'boolean':
        return value === 'true';
      case 'datetime':
      case 'date':
        return new Date(value);
      default:
        return value;
    }
  };

  const getQuery = (extras: FilterQuery[] = []): FilterTree | undefined => {
    const allFilters = [...appliedFilters.value, ...extras];
    if (allFilters.length === 0) return undefined;
    const tree = buildFilterTree(allFilters);

    return tree;
  };

  onMounted(async () => {
    if (type) {
      await fetchAvailableFilters();
      const urlFilters = extractFiltersFromRoute();
      if (urlFilters.length > 0) {
        applyFilters(urlFilters);
      } else {
        applyFilters(appliedFilters.value);
      }
    }
  });

  onBeforeUnmount(() => {
    clearFilters();
  });

  return {
    appliedFilters,
    applyFilters,
    clearFilters,
    emits: FilterEvents,
    events: $bus,
    filterOptions: availableFilters,
    getQuery,
    isLoading,
    options,
    removeFilter,
    value: appliedFilters,
  };
}
